import React, { useCallback, useState } from "react";

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input
} from "@chakra-ui/react";

import { format } from "date-fns";
import DatePicker from "react-datepicker";

import { Form } from "@/components/forms/Form/Form";
import { useSideBar } from "@/components/layout/AppLayout";
import { SideBar } from "@/components/layout/SideBar";
import useCrudListFilter, {
  FilterFields
} from "@/hooks/crud/useCrudListFilter";

import "react-datepicker/dist/react-datepicker.css";
import { AdvancedFilters } from "./CrudListAdvancedFilters";

const initialValues: FilterFields = {
  supplier: "",
  total: "",
  dateFrom: "",
  dateTo: "",
  accountCode: "",
  vatAmount: "",
  uploadedDateFrom: "",
  uploadedDateTo: "",
  source: "",
  uploadedBy: "",
  invoiceId: "",
  status: []
};

interface CrudListFilterBarProps {
  statusCounts: Record<string, number>;
  lastChosenStatus: string;
  hideAdvancedFilters?: boolean;
  filterLabels?: {
    supplier: string;
    total: string;
    dateFrom: string;
    dateTo: string;
  };
}

const initialFilterLabels: CrudListFilterBarProps["filterLabels"] = {
  supplier: "Supplier",
  total: "Invoice Amount",
  dateFrom: "Invoice Date From",
  dateTo: "Invoice Date To"
};

const formatDate = (date: Date | null): string | undefined => {
  if (!date) return undefined;
  if (Number.isNaN(date.getTime())) return undefined;
  return format(date, "yyyy-MM-dd");
};

export const CrudListFilterBar: React.FC<CrudListFilterBarProps> = ({
  statusCounts,
  lastChosenStatus,
  hideAdvancedFilters = false,
  filterLabels = initialFilterLabels
}) => {
  const { applyFilters, clearFilters, currentFilters } = useCrudListFilter();
  const { toggleSideBar, isSideBarVisible } = useSideBar();

  const [filters, setFilters] = useState<FilterFields>(currentFilters);
  const [startDate, setStartDate] = useState<Date | null>(
    filters.dateFrom ? new Date(filters.dateFrom) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    filters.dateTo ? new Date(filters.dateTo) : null
  );
  const [uploadedStartDate, setUploadedStartDate] = useState<Date | null>(
    filters.uploadedDateFrom ? new Date(filters.uploadedDateFrom) : null
  );
  const [uploadedEndDate, setUploadedEndDate] = useState<Date | null>(
    filters.uploadedDateTo ? new Date(filters.uploadedDateTo) : null
  );
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({
    status: true,
    accountCode: true,
    vatAmount: true,
    total: true,
    uploadedDateRange: true,
    source: true,
    uploadedBy: true,
    invoiceId: true
  });
  const [resetCounter, setResetCounter] = useState(0);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = event.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleStartDateChange = useCallback((date: Date | null) => {
    setStartDate(date);
    setFilters((prev) => ({ ...prev, dateFrom: formatDate(date) }));
  }, []);

  const handleEndDateChange = useCallback((date: Date | null) => {
    setEndDate(date);
    setFilters((prev) => ({ ...prev, dateTo: formatDate(date) }));
  }, []);

  const handleUploadedStartDateChange = useCallback((date: Date | null) => {
    setUploadedStartDate(date);
    setFilters((prev) => ({ ...prev, uploadedDateFrom: formatDate(date) }));
  }, []);

  const handleUploadedEndDateChange = useCallback((date: Date | null) => {
    setUploadedEndDate(date);
    setFilters((prev) => ({ ...prev, uploadedDateTo: formatDate(date) }));
  }, []);

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault();
      }
      applyFilters(filters);
      setCollapsedSections({
        status: true,
        accountCode: true,
        vatAmount: true,
        total: true,
        uploadedDateRange: true,
        source: true,
        uploadedBy: true,
        invoiceId: true
      });
      setResetCounter((prev) => prev + 1);
    },
    [applyFilters, filters]
  );

  const handleClear = useCallback(() => {
    setFilters({});
    setStartDate(null);
    setEndDate(null);
    setUploadedStartDate(null);
    setUploadedEndDate(null);
    clearFilters();
    setCollapsedSections({
      status: true,
      accountCode: true,
      vatAmount: true,
      total: true,
      uploadedDateRange: true,
      source: true,
      uploadedBy: true,
      invoiceId: true
    });
    setResetCounter((prev) => prev + 1);
  }, [clearFilters]);

  const handleToggleSideBar = () => {
    if (isSideBarVisible) {
      clearFilters();
    }
    toggleSideBar();
  };

  // Function to check if there are any active filters
  const hasActiveFilters = () => {
    // Check basic filters
    if (filters.supplier) return true;
    if (filters.total) return true;
    if (filters.dateFrom) return true;
    if (filters.dateTo) return true;

    // Check advanced filters
    if (filters.accountCode) return true;
    if (filters.vatAmount) return true;
    if (filters.source) return true;
    if (filters.status && filters.status.length > 0 && !lastChosenStatus)
      return true;
    if (uploadedStartDate || uploadedEndDate) return true;
    if (filters.uploadedBy) return true;
    if (filters.invoiceId) return true;

    return false;
  };

  return (
    <Box>
      <Flex alignItems="center" gap={4}>
        <Box flex={1}>
          <Form onSubmit={() => handleSubmit()} initialValues={initialValues}>
            <HStack spacing={4} alignItems="end">
              <Flex gap={4} alignItems="flex-end">
                <FormControl>
                  <FormLabel>{filterLabels.supplier}:</FormLabel>
                  <Input
                    backgroundColor="white"
                    name="supplier"
                    value={filters.supplier || ""}
                    onChange={handleInputChange}
                    placeholder="Filter by supplier..."
                  />
                </FormControl>
                <FormControl>
                  <FormLabel size="sm">{filterLabels.total}:</FormLabel>
                  <Input
                    backgroundColor="white"
                    name="total"
                    value={filters.total || ""}
                    onChange={handleInputChange}
                    placeholder="Filter by amount..."
                  />
                </FormControl>
              </Flex>
              <Flex alignItems="flex-end" gap={4}>
                <FormControl>
                  <FormLabel>{filterLabels.dateFrom}</FormLabel>
                  <Box
                    as={DatePicker}
                    selected={startDate}
                    onChange={handleStartDateChange}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="dd/MM/yyyy"
                    customInput={<Input />}
                    placeholderText="From date..."
                    css={{
                      width: "100%",
                      height: "2.5rem",
                      fontSize: "1rem",
                      color: "#2d3748",
                      "&::placeholder": {
                        color: "#a0aec0"
                      },
                      backgroundColor: "white"
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{filterLabels.dateTo}</FormLabel>
                  <Box
                    as={DatePicker}
                    selected={endDate}
                    onChange={handleEndDateChange}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    {...(startDate ? { minDate: startDate } : {})}
                    dateFormat="dd/MM/yyyy"
                    customInput={<Input />}
                    placeholderText="To date..."
                    css={{
                      width: "100%",
                      height: "2.5rem",
                      fontSize: "1rem",
                      color: "#2d3748",
                      "&::placeholder": {
                        color: "#a0aec0"
                      },
                      backgroundColor: "white"
                    }}
                  />
                </FormControl>
              </Flex>
              <Flex justifyContent="flex-end" gap={4}>
                <Box
                  transition="all 0.3s ease"
                  opacity={isSideBarVisible ? 0 : 1}
                  width={isSideBarVisible ? "0px" : "90px"}
                  minWidth={isSideBarVisible ? "0px" : "90px"}
                  overflow="hidden"
                  style={{
                    transitionProperty: "opacity, width, min-width"
                  }}
                >
                  <Button height="2.5rem" type="submit" colorScheme="blue">
                    Search
                  </Button>
                </Box>
                {!hideAdvancedFilters && (
                  <Button
                    height="2.5rem"
                    type="button"
                    onClick={handleToggleSideBar}
                  >
                    Advanced Filters
                  </Button>
                )}
                {hasActiveFilters() && (
                  <Box
                    transition="all 0.3s ease"
                    opacity={isSideBarVisible ? 0 : 1}
                    width={isSideBarVisible ? "0px" : "120px"}
                    minWidth={isSideBarVisible ? "0px" : "120px"}
                    overflow="hidden"
                    style={{
                      transitionProperty: "opacity, width, min-width"
                    }}
                  >
                    <Button
                      height="2.5rem"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={handleClear}
                    >
                      Clear Filters
                    </Button>
                  </Box>
                )}
              </Flex>
            </HStack>
          </Form>
        </Box>
      </Flex>
      <SideBar isVisible={isSideBarVisible}>
        <AdvancedFilters
          filters={filters}
          handleInputChange={handleInputChange}
          uploadedStartDate={uploadedStartDate}
          uploadedEndDate={uploadedEndDate}
          handleUploadedStartDateChange={handleUploadedStartDateChange}
          handleUploadedEndDateChange={handleUploadedEndDateChange}
          handleClear={handleClear}
          handleSubmit={handleSubmit}
          collapsedSections={collapsedSections}
          setCollapsedSections={setCollapsedSections}
          statusCounts={statusCounts}
          lastChosenStatus={lastChosenStatus}
          resetCounter={resetCounter}
        />
      </SideBar>
    </Box>
  );
};
